import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { SessionState } from '../types/auth.types'

/** Keep in sync with `MANDATORY_SESSION_EXTENSION_COUNT_REDUX_CEILING` in `libs/ecrm-session/src/lib/mandatorySession.constants.ts`. */
const MAX_SESSION_EXTENSION_COUNT_REDUX = 24

const initialState: SessionState = {
  isAuthenticated: false,
  token: null,
  forceMsalPromptNextLogin: false,
  lastActivityAtMs: 0,
  sessionLoginAtMs: 0,
  maxSessionExtensionCount: 0,
  refreshToken: null,
  pkce_code_verifier: null,
}

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setSessionCredentials: (
      state,
      action: PayloadAction<{
        isAuthenticated: boolean
        token: string
        sessionLoginAtMs?: number
        maxSessionExtensionCount?: number
        refreshToken?: string | null
        pkce_code_verifier?: string | null
      }>
    ) => {
      state.isAuthenticated = action.payload.isAuthenticated
      state.token = action.payload.token
      if (action.payload.sessionLoginAtMs !== undefined) {
        state.sessionLoginAtMs = action.payload.sessionLoginAtMs ?? 0
      }
      if (action.payload.refreshToken !== undefined) {
        state.refreshToken = action.payload.refreshToken
      }
      if (action.payload.pkce_code_verifier !== undefined) {
        state.pkce_code_verifier = action.payload.pkce_code_verifier
      }
      if (action.payload.maxSessionExtensionCount !== undefined) {
        state.maxSessionExtensionCount = action.payload.maxSessionExtensionCount
      }
    },
    clearSession: () => initialState,
    setMaxSessionExtensionCount: (state, action: PayloadAction<number>) => {
      const capped = Math.min(
        MAX_SESSION_EXTENSION_COUNT_REDUX,
        Math.max(0, Math.floor(action.payload))
      )
      state.maxSessionExtensionCount = Math.max(
        state.maxSessionExtensionCount ?? 0,
        capped
      )
    },
    setSessionLastActivityAtMs: (state, action: PayloadAction<number>) => {
      state.lastActivityAtMs = action.payload
    },
    setForceMsalPromptNextLogin: (state, action: PayloadAction<boolean>) => {
      state.forceMsalPromptNextLogin = action.payload
    },
    clearForceMsalPromptNextLogin: (state) => {
      state.forceMsalPromptNextLogin = false
    },
    updateToken: (state, action: PayloadAction<{ token: string }>) => {
      state.token = action.payload.token
    },
    updateRefreshToken: (
      state,
      action: PayloadAction<{ refreshToken: string }>
    ) => {
      state.refreshToken = action.payload.refreshToken
    },
  },
})

export const {
  setSessionCredentials,
  clearSession,
  setSessionLastActivityAtMs,
  setForceMsalPromptNextLogin,
  clearForceMsalPromptNextLogin,
  updateToken,
  updateRefreshToken,
  setMaxSessionExtensionCount,
} = sessionSlice.actions

export default sessionSlice.reducer
